import React from "react";
import { Link } from "react-router-dom";
import Nav from "./Nav";

// Location archive images — original photographs only, categorised from
// their visual content. No private addresses.
const LOCATIONS = [
  {
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/8kli93c0_club%2030.webp",
    label: "Nightlife",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/x345hxpw_mansion50.webp",
    label: "Premium Residence",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/9bjjk5q2_ice10.webp",
    label: "Sports Arena",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/wdf1gsdl_club20.webp",
    label: "Urban",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/gshluacx_club020.webp",
    label: "Atmospheric",
  },
];

// Split into two rows for the opposite-direction marquees.
const ROW_A = LOCATIONS;
const ROW_B = [...LOCATIONS.slice(2), ...LOCATIONS.slice(0, 2)]; // rotated

// A shared editorial section unit. Now with NO numbered section label —
// heading is the anchor, kicker removed per spec.
const Block = ({ title, children, testId }) => (
  <section
    className="relative w-full py-20 md:py-28 border-t"
    style={{ borderColor: "#141414", background: "rgba(8, 8, 8, 0.9)" }}
    data-testid={testId}
  >
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-12 gap-8 md:gap-14 items-start">
        <div className="col-span-12 md:col-span-4">
          <h2
            className="leading-[0.95]"
            style={{
              fontSize: "clamp(1.85rem, 3.6vw, 2.75rem)",
              letterSpacing: "-0.03em",
              fontWeight: 500,
              color: "#F5F5F3",
            }}
          >
            {title}
          </h2>
        </div>
        <div className="col-span-12 md:col-span-7 md:col-start-6">
          {children}
        </div>
      </div>
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li
    className="flex gap-4 leading-[1.55]"
    style={{
      color: "#F5F5F3",
      opacity: 0.88,
      fontSize: "clamp(0.95rem, 1.15vw, 1.1rem)",
    }}
  >
    <span
      aria-hidden
      className="shrink-0 mt-[10px]"
      style={{
        width: 4,
        height: 4,
        borderRadius: 999,
        background: "#FF6A3D",
        display: "inline-block",
      }}
    />
    <span>{children}</span>
  </li>
);

// One tile of the marquee — 4:3 photograph with a small "LOCATION ARCHIVE"
// eyebrow and a category label.
const LocationTile = ({ src, label }) => (
  <div
    className="shrink-0 relative overflow-hidden"
    style={{
      width: "clamp(280px, 34vw, 460px)",
      aspectRatio: "4 / 3",
      borderRadius: "14px",
      background: "#0d0d0d",
      border: "1px solid rgba(255,255,255,0.05)",
    }}
  >
    <img
      src={src}
      alt=""
      aria-hidden
      draggable={false}
      className="absolute inset-0 w-full h-full object-cover"
      style={{ filter: "saturate(0.95) contrast(1.03)" }}
    />
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(180deg, rgba(8,8,8,0.15) 0%, rgba(8,8,8,0) 30%, rgba(8,8,8,0.05) 60%, rgba(8,8,8,0.75) 100%)",
      }}
    />
    <div className="absolute inset-0 flex flex-col justify-between p-4 md:p-5">
      <div
        className="text-[9px] tracking-[0.34em] uppercase"
        style={{ color: "rgba(245,245,243,0.55)", fontWeight: 500 }}
      >
        Location Archive
      </div>
      <div
        className="text-base md:text-lg"
        style={{
          color: "#F5F5F3",
          fontWeight: 500,
          letterSpacing: "-0.015em",
          textShadow: "0 2px 20px rgba(0,0,0,0.6)",
        }}
      >
        {label}
      </div>
    </div>
  </div>
);

// Full-width infinite marquee. Two copies of the row rendered back-to-back
// so translating -50% loops seamlessly. Hover pauses the animation.
const LocationMarquee = ({ items, direction = "left" }) => {
  const set = items.concat(items);
  return (
    <div
      className="lr-marquee-wrap relative w-full overflow-hidden"
      style={{ marginTop: "1rem" }}
    >
      {/* Edge fades */}
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 pointer-events-none"
        style={{
          width: "80px",
          background:
            "linear-gradient(to right, rgba(8,8,8,0.95), rgba(8,8,8,0))",
          zIndex: 2,
        }}
      />
      <div
        aria-hidden
        className="absolute inset-y-0 right-0 pointer-events-none"
        style={{
          width: "80px",
          background:
            "linear-gradient(to left, rgba(8,8,8,0.95), rgba(8,8,8,0))",
          zIndex: 2,
        }}
      />
      <div className={`lr-marquee dir-${direction}`}>
        {set.map((t, i) => (
          <LocationTile key={`${direction}-${i}`} src={t.src} label={t.label} />
        ))}
      </div>
    </div>
  );
};

export const ProductionInPoland = () => {
  return (
    <div
      className="relative min-h-screen"
      style={{
        background: "#080808",
        color: "#F5F5F3",
        width: "100%",
        maxWidth: "100vw",
        overflowX: "clip",
      }}
      data-testid="page-production-in-poland"
    >
      <div className="relative" style={{ zIndex: 10 }}>
        <Nav />

        {/* HERO */}
        <section
          className="relative w-full pt-40 md:pt-56 pb-20 md:pb-24"
          data-testid="pip-hero"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div
              className="text-[11px] tracking-[0.32em] uppercase mb-8"
              style={{ color: "#A1A1AA", fontWeight: 500 }}
            >
              <span style={{ color: "#FF6A3D" }}>—</span>&nbsp;&nbsp;Livingroom
              in Poland
            </div>
            <h1
              className="leading-[0.9] max-w-5xl"
              style={{
                fontSize: "clamp(2.75rem, 8vw, 6.75rem)",
                letterSpacing: "-0.045em",
                fontWeight: 500,
                color: "#F5F5F3",
              }}
              data-testid="pip-title"
            >
              Production in Poland.
            </h1>
            <p
              className="mt-10 md:mt-14 max-w-3xl leading-[1.35]"
              style={{
                fontSize: "clamp(1.25rem, 2.2vw, 1.85rem)",
                letterSpacing: "-0.02em",
                color: "#F5F5F3",
                fontWeight: 500,
              }}
              data-testid="pip-tagline"
            >
              A local production partner for scripted series, international
              formats, mobile-first entertainment and scalable production
              models.
            </p>
            <p
              className="mt-8 max-w-2xl leading-[1.6]"
              style={{
                fontSize: "clamp(1rem, 1.3vw, 1.15rem)",
                color: "#F5F5F3",
                opacity: 0.78,
                fontWeight: 400,
              }}
              data-testid="pip-intro"
            >
              Livingroom Pictures offers end-to-end production support in
              Poland, combining local infrastructure, creative talent and
              flexible production units built around the needs of each
              project.
            </p>
          </div>
        </section>

        {/* LOCATIONS */}
        <section
          className="relative w-full py-20 md:py-28 border-t"
          style={{ borderColor: "#141414", background: "rgba(8, 8, 8, 0.9)", overflow: "hidden" }}
          data-testid="pip-locations"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-12 gap-8 md:gap-14 items-start">
              <div className="col-span-12 md:col-span-4">
                <h2
                  className="leading-[0.95]"
                  style={{
                    fontSize: "clamp(1.85rem, 3.6vw, 2.75rem)",
                    letterSpacing: "-0.03em",
                    fontWeight: 500,
                    color: "#F5F5F3",
                  }}
                >
                  A strong location base.
                </h2>
              </div>
              <div className="col-span-12 md:col-span-7 md:col-start-6">
                <ul className="space-y-4">
                  <Bullet>
                    Access to a wide range of visually distinctive locations
                    across Poland.
                  </Bullet>
                  <Bullet>
                    Urban, residential, industrial, premium, nature-based and
                    character-driven spaces.
                  </Bullet>
                  <Bullet>
                    Suitable for scripted, unscripted and mobile-first
                    productions.
                  </Bullet>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-14 md:mt-20 space-y-5 md:space-y-7" data-testid="location-marquee">
            <LocationMarquee items={ROW_A} direction="left" />
            <div className="hidden md:block">
              <LocationMarquee items={ROW_B} direction="right" />
            </div>
          </div>
        </section>

        <Block title="Local cast and talent." testId="pip-cast">
          <ul className="space-y-4">
            <Bullet>Access to local actors and on-screen talent.</Bullet>
            <Bullet>
              Flexible casting possibilities for scripted, unscripted and
              mobile-first productions.
            </Bullet>
            <Bullet>
              Ability to support both performance-driven drama and
              format-based entertainment.
            </Bullet>
          </ul>
        </Block>

        <Block title="Scripted and unscripted expertise." testId="pip-expertise">
          <ul className="space-y-4">
            <Bullet>
              Directors, DPs and production professionals with experience
              across different production models.
            </Bullet>
            <Bullet>
              Teams able to adapt to both cinematic storytelling and
              structured entertainment formats.
            </Bullet>
            <Bullet>
              Flexible production units built around the scale and needs of
              each project.
            </Bullet>
          </ul>
        </Block>

        <Block title="Props and costume resources." testId="pip-props">
          <ul className="space-y-4">
            <Bullet>
              Access to in-house and production-ready prop and costume
              resources.
            </Bullet>
            <Bullet>
              Faster preparation and more flexible production workflows.
            </Bullet>
            <Bullet>
              Useful for both scripted storytelling and rapid production
              environments.
            </Bullet>
          </ul>
        </Block>

        <Block title="Virtual production and LED partnerships." testId="pip-virtual">
          <ul className="space-y-4">
            <Bullet>
              Partner relationships with virtual production and LED screen
              providers.
            </Bullet>
            <Bullet>
              Ability to integrate virtual workflows where creatively or
              logistically useful.
            </Bullet>
            <Bullet>
              Suitable for both controlled studio production and more
              advanced visual setups.
            </Bullet>
          </ul>
        </Block>

        <Block title="Why produce with Livingroom in Poland." testId="pip-why">
          <ul className="space-y-4">
            <Bullet>Local production knowledge on the ground.</Bullet>
            <Bullet>Scalable production units built around each project.</Bullet>
            <Bullet>
              Experience across vertical drama, scripted work and
              entertainment formats.
            </Bullet>
            <Bullet>A fast, flexible and audience-aware production approach.</Bullet>
            <Bullet>A team that understands both storytelling and execution.</Bullet>
          </ul>
        </Block>

        {/* LEGAL SUPPORT */}
        <Block title="Full production legal support." testId="pip-legal">
          <p
            className="leading-[1.6] mb-8"
            style={{
              color: "#F5F5F3",
              opacity: 0.88,
              fontSize: "clamp(1rem, 1.3vw, 1.15rem)",
            }}
            data-testid="pip-legal-copy"
          >
            Together with our trusted legal partners, we support the complete
            legal framework required for production in Poland and
            international delivery &mdash; from contracts and releases to
            permits, rights clearance, data protection, immigration and work
            authorisation.
          </p>
          <ul className="space-y-4">
            <Bullet>Contracts, releases and chain of title.</Bullet>
            <Bullet>Production permits and local compliance.</Bullet>
            <Bullet>Cast and crew employment documentation.</Bullet>
            <Bullet>Immigration and work-authorisation coordination.</Bullet>
            <Bullet>Data protection and privacy compliance.</Bullet>
            <Bullet>
              Rights clearance and international delivery requirements.
            </Bullet>
          </ul>
          <p
            className="mt-8 leading-[1.55] text-[13px] md:text-sm"
            style={{
              color: "#A1A1AA",
              fontWeight: 400,
            }}
          >
            Livingroom Pictures coordinates this support together with trusted
            legal partners under applicable Polish law and international
            distribution requirements.
          </p>
        </Block>

        {/* FINAL CTA */}
        <section
          className="relative w-full py-28 md:py-40"
          style={{
            borderTop: "1px solid #141414",
            background:
              "radial-gradient(ellipse 80% 60% at 50% 30%, rgba(255,106,61,0.06) 0%, rgba(255,106,61,0) 60%), rgba(8, 8, 8, 0.94)",
          }}
          data-testid="pip-cta"
        >
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2
              className="leading-[0.95]"
              style={{
                fontSize: "clamp(2rem, 5vw, 3.75rem)",
                letterSpacing: "-0.035em",
                fontWeight: 500,
                color: "#F5F5F3",
              }}
            >
              Planning a production in Poland?
            </h2>
            <p
              className="mt-8 mx-auto leading-[1.5]"
              style={{
                fontSize: "clamp(1.05rem, 1.4vw, 1.25rem)",
                color: "#F5F5F3",
                opacity: 0.82,
                fontWeight: 400,
                maxWidth: "560px",
              }}
            >
              Let&rsquo;s talk about your format, timeline and production
              needs.
            </p>

            <div className="mt-14 flex flex-wrap items-center justify-center gap-10">
              <Link
                to="/#contact"
                className="lr-btn"
                data-testid="pip-contact-btn"
              >
                <span>Let&rsquo;s talk</span>
                <span aria-hidden>→</span>
              </Link>
              <a
                href="https://www.linkedin.com/company/109712897"
                target="_blank"
                rel="noopener noreferrer"
                className="lr-contact-mail inline-block"
                style={{
                  color: "#F5F5F3",
                  fontSize: "clamp(0.95rem, 1.15vw, 1.05rem)",
                  letterSpacing: "-0.005em",
                  fontWeight: 500,
                  borderBottom: "1px solid rgba(245,245,243,0.25)",
                  paddingBottom: 4,
                }}
                data-testid="pip-linkedin"
              >
                LinkedIn &nbsp;<span style={{ color: "#FF6A3D" }}>→</span>
              </a>
            </div>

            <div
              className="mt-16 text-[10px] tracking-[0.3em] uppercase"
              style={{ color: "#A1A1AA" }}
            >
              <Link to="/" data-testid="pip-back-home" style={{ color: "#A1A1AA" }}>
                ← Back to homepage
              </Link>
            </div>
          </div>
        </section>

        <footer
          className="w-full py-10 px-6 md:px-12 flex flex-wrap items-center justify-between gap-4"
          style={{ borderTop: "1px solid #141414", background: "#080808" }}
        >
          <span
            className="text-lg"
            style={{ letterSpacing: "-0.01em", fontWeight: 600 }}
          >
            Livingroom<span style={{ color: "#FF6A3D" }}>.</span>
          </span>
          <span
            className="text-[10px] tracking-[0.3em] uppercase"
            style={{ color: "#A1A1AA" }}
          >
            MMXXV · Warsaw · Poland
          </span>
        </footer>
      </div>
    </div>
  );
};

export default ProductionInPoland;
